import { Box, Typography, Table, TableHead, TableRow, TableBody } from '@mui/material';
import {
  DialogContainer,
  DialogBody,
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
} from '../../../../common/components';
import { useExcelExport } from '../../../habitat-99/hooks/useExcelExport';
import { useIntermediateEntitiesPDF } from '../../hooks/useIntermediateEntitiesPDF';
import StatisticsDialogTitle from '../../../habitat-99/components/common/StatisticsDialogTitle';
import { formatCurrency } from '../../../habitat-99/utils/statisticsUtils';

const IntermediateEntitiesCollectionModal = ({ open, handleClose, header = {}, results = [] }) => {
  const { exportMultipleSheets } = useExcelExport();
  const { exportIntermediateCollectionPDF } = useIntermediateEntitiesPDF();

  const normalizedHeader = header && !Array.isArray(header) ? header : {};
  const hasData = Array.isArray(results) && results.length > 0;
  const summaryTotals = normalizedHeader?.overall_totals || { social_fee: 0, pei: 0, total: 0 };

  const buildSummarySheet = () => {
    if (!hasData) {
      return [{ 'Mensaje': 'Sin datos disponibles' }];
    }

    return results.map((entity) => ({
      'Entidad intermedia': entity.legal_entity_name,
      'Cuotas sociales': Number(entity?.totals?.social_fee ?? 0),
      'P.E.I': Number(entity?.totals?.pei ?? 0),
      Total: Number(entity?.totals?.total ?? 0),
    }));
  };

  const buildDetailSheet = () => {
    if (!hasData) {
      return [];
    }

    const detailRows = [];

    results.forEach((entity) => {
      entity?.groups?.forEach((group) => {
        group?.coupons?.forEach((coupon) => {
          detailRows.push({
            'Entidad intermedia': entity.legal_entity_name,
            Grupo: group.group_name,
            Orden: coupon.contract_number,
            Suscriptor: coupon.subscriber_name,
            'Cta. Simple': coupon.fee_number1,
            'Cta. Doble': coupon.fee_number2,
            'Fecha de pago': coupon.cancellation_date,
            'Cuota Social': Number(coupon.social_fee_amount ?? 0),
            'P.E.I': Number(coupon.pei_amount ?? 0),
            Total: Number(coupon.total_amount ?? 0),
          });
        });
      });
    });

    if (!detailRows.length) {
      detailRows.push({ Mensaje: 'Sin detalle disponible' });
    }

    return detailRows;
  };

  const handleExportToExcel = () => {
    const sheets = [
      { name: 'Resumen', data: buildSummarySheet() },
    ];

    const detailSheet = buildDetailSheet();
    if (detailSheet.length) {
      sheets.push({ name: 'Detalle', data: detailSheet });
    }

    exportMultipleSheets(sheets, 'RecaudacionEntidadesIntermedias');
  };

  const handleExportToPDF = () => {
    const fallbackEntity = {
      legal_entity_name: normalizedHeader?.legal_entity_name || '',
      totals: summaryTotals,
      groups: [],
    };

    exportIntermediateCollectionPDF({
      header: normalizedHeader,
      entities: hasData ? results : [fallbackEntity],
      filename: 'RecaudacionEntidadesIntermedias',
    });
  };

  const renderGroupTable = (group) => (
    <GeneralTableBox sx={{ mt: 2, minHeight: 'unset', backgroundColor: '#fff' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <HeadTableCell>Orden</HeadTableCell>
            <HeadTableCell>Apellido y Nombre</HeadTableCell>
            <HeadTableCell>Cta. Simple</HeadTableCell>
            <HeadTableCell>Cta. Doble</HeadTableCell>
            <HeadTableCell>Fecha pago</HeadTableCell>
            <HeadTableCell>Cuota Social</HeadTableCell>
            <HeadTableCell>P.E.I</HeadTableCell>
            <HeadTableCell>Total</HeadTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {group?.coupons?.map((coupon) => (
            <TableRow key={coupon.coupon_uuid || `${coupon.contract_number}-${coupon.fee_number1}-${coupon.fee_number2}`}>
              <RowTableCell>{coupon.contract_number ?? '-'}</RowTableCell>
              <RowTableCell>{coupon.subscriber_name || 'Sin titular'}</RowTableCell>
              <RowTableCell>{coupon.fee_number1 ?? '-'}</RowTableCell>
              <RowTableCell>{coupon.fee_number2 ?? '-'}</RowTableCell>
              <RowTableCell>{coupon.cancellation_date || '-'}</RowTableCell>
              <RowTableCell align="right">{formatCurrency(coupon.social_fee_amount || 0)}</RowTableCell>
              <RowTableCell align="right">{formatCurrency(coupon.pei_amount || 0)}</RowTableCell>
              <RowTableCell align="right">{formatCurrency(coupon.total_amount || 0)}</RowTableCell>
            </TableRow>
          ))}
          <TableRow>
            <RowTableCell colSpan={5} sx={{ fontWeight: 'bold' }}>
              Total del grupo
            </RowTableCell>
            <RowTableCell align="right" sx={{ fontWeight: 'bold' }}>
              {formatCurrency(group?.totals?.social_fee || 0)}
            </RowTableCell>
            <RowTableCell align="right" sx={{ fontWeight: 'bold' }}>
              {formatCurrency(group?.totals?.pei || 0)}
            </RowTableCell>
            <RowTableCell align="right" sx={{ fontWeight: 'bold' }}>
              {formatCurrency(group?.totals?.total || 0)}
            </RowTableCell>
          </TableRow>
        </TableBody>
      </Table>
    </GeneralTableBox>
  );

  if (!normalizedHeader || Object.keys(normalizedHeader).length === 0) {
    return null;
  }

  return (
    <DialogContainer open={open} onClose={handleClose} size="fullWidth">
      <StatisticsDialogTitle
        title="Recaudación entidades intermedias"
        onExportExcel={handleExportToExcel}
        onExportPDF={handleExportToPDF}
        onClose={handleClose}
      />
      <DialogBody>
        <Box
          sx={{
            mb: 3,
            textAlign: 'center',
            border: '1px solid #4a90e2',
            p: 2,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333', mb: 1 }}>
            {normalizedHeader.constructora_info}
          </Typography>
          <Typography variant="body2" sx={{ color: '#555', fontStyle: 'italic', mb: 0.5 }}>
            Actualizado al: {normalizedHeader.updated_at_date || '-'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#555' }}>
            {normalizedHeader.date_filter_text || 'Sin filtros de fecha'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#555', mt: 0.5 }}>
            Plan: {normalizedHeader.plan_name || '-'} | Entidad seleccionada: {normalizedHeader.legal_entity_name || '-'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
          {[
            { label: 'Cuotas Sociales recaudadas', value: summaryTotals.social_fee || 0 },
            { label: 'P.E.I recaudados', value: summaryTotals.pei || 0 },
            { label: 'Total a rendir', value: summaryTotals.total || 0 },
          ].map((item) => (
            <Box
              key={item.label}
              sx={{
                flex: '1 1 200px',
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                p: 2,
                backgroundColor: '#f9fafc',
              }}
            >
              <Typography variant="subtitle2" sx={{ color: '#555' }}>
                {item.label}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
                {formatCurrency(item.value)}
              </Typography>
            </Box>
          ))}
        </Box>

        {!hasData && (
          <Typography variant="body1" sx={{ textAlign: 'center', mt: 4 }}>
            No se encontraron cupones para los filtros seleccionados.
          </Typography>
        )}

        {hasData &&
          results.map((entity) => (
            <Box key={entity.legal_entity_uuid || entity.legal_entity_name} sx={{ mb: 4 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
                  {entity.legal_entity_name}
                </Typography>
                <Typography variant="body2" sx={{ color: '#777' }}>
                  Total entidad: {formatCurrency(entity?.totals?.total || 0)}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                {[
                  { label: 'Cuotas Sociales', value: entity?.totals?.social_fee || 0 },
                  { label: 'P.E.I', value: entity?.totals?.pei || 0 },
                  { label: 'Total Recaudado', value: entity?.totals?.total || 0 },
                ].map((item) => (
                  <Box
                    key={`${entity.legal_entity_uuid}-${item.label}`}
                    sx={{
                      flex: '1 1 200px',
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      p: 1.5,
                      backgroundColor: '#fff',
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#777' }}>
                      {item.label}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {formatCurrency(item.value)}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {entity?.groups?.map((group) => (
                <Box key={`${entity.legal_entity_uuid}-${group.group_name}`} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Grupo {group.group_name}
                  </Typography>
                  {renderGroupTable(group)}
                </Box>
              ))}
            </Box>
          ))}
      </DialogBody>
    </DialogContainer>
  );
};

export default IntermediateEntitiesCollectionModal;
